/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';

import { Button, Dialog, InlineNotification, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { AnswerCorrectIcon, LockIcon, SuccessIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { isYearly } from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import ReactPriceDisplay from 'bundles/payments-common/components/ReactPriceDisplay';
import type { ProductType } from 'bundles/payments/common/ProductType';
import { COURSERA_PLUS_SUBSCRIPTION } from 'bundles/payments/common/ProductType';
import { getPrivacyPolicy, getTermsOfUseLink } from 'bundles/payments/common/staticLinks';
import {
  isCourseraPlusOrIsSpecializationSubscription,
  useEventTracking,
  useReactivateCanceledSubscription,
} from 'bundles/subscriptions/utils/cancellationUndoUtils';

import _t from 'i18n!nls/subscriptions';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const styles = {
  dialog: css`
    position: relative;

    .cds-Dialog-dialog {
      max-width: 700px !important;
    }
  `,
  stepWrapper: css`
    list-style: none;
    padding-left: 0;
    overflow: hidden;
  `,
  step: css`
    display: flex;
    margin-bottom: 8px;
  `,
  stepContent: css`
    margin-bottom: 10px;
    margin-top: -2px;
  `,
  stepIcon: css`
    color: blue;
    margin-right: 8px;
    position: relative;

    &::after {
      display: inline-block;
      content: '';
      position: absolute;
      top: 18.5px;
      left: 8px;
      width: 10px;
      height: calc(100% - 9px);
      border-left: 4px solid #0057d2;
    }
  `,
  lastIcon: css`
    &::after {
      display: none;
    }
  `,
  paymentDetails: css`
    padding: 20px 0;
    margin: 20px auto;
    border: 1px solid lightgray;
  `,
  underline: css`
    text-decoration: underline;
  `,
  paymentDetailsTitle: css`
    padding: 0 20px;
  `,
  secureTextWrapper: css`
    color: red;
    display: flex;
    margin-top: 8px;
    align-items: center;
    font-size: 18px;
    padding: 0 20px;
  `,
  lockIcon: css`
    margin-right: 8px;
    position: relative;

    &::before {
      display: inline-block;
      content: '';
      position: absolute;
      top: -3px;
      right: -6px;
      height: 28px;
      width: 28px;
      background-color: #c7fbe7;
      border-radius: 50%;
      opacity: 0.4;
    }
  `,
  walletDetailsRow: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    padding: 0 20px;
  `,
  todaysTotalRow: css`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-top: 8px;
    padding: 10px 20px;
  `,
  content: css`
    ${breakpoints.down('xs')} {
      padding: 0;
      margin: var(--cds-spacing-200) var(--cds-spacing-400);
    }
  `,
  action: css`
    display: flex;
    gap: 16px;
    padding-top: 16px;
    ${breakpoints.down('xs')} {
      padding: 0;
      margin: var(--cds-spacing-400);
      flex-direction: column;
    }
  `,
  todaysPrice: css`
    text-align: right;

    @media (max-width: 323px) {
      text-align: left;
    }
  `,
  grayText: css`
    color: #636363;
  `,
  title: css`
    padding-bottom: 24px;
    margin-top: var(--cds-spacing-200);
    ${breakpoints.down('xs')} {
      padding: 0;
      margin: var(--cds-spacing-200) var(--cds-spacing-400);
    }
  `,
};

type ReactivateSubscriptionLegalCopyInputType = {
  priceDisplay: JSX.Element;
  nextBillingDate: string;
  isCourseraPlusAnnual: boolean;
};
const getPriceDisplayFromAmount = (amount: number | string, currency: string) => (
  <ReactPriceDisplay value={Number(amount)} currency={currency} hideCurrencyCode={false} />
);

const ReactivateSubscriptionLegalCopy = ({
  priceDisplay,
  nextBillingDate,
  isCourseraPlusAnnual,
}: ReactivateSubscriptionLegalCopyInputType) => {
  const youWillBeCharged = (
    <strong>
      <FormattedMessage
        message={_t(
          "You'll be charged {nextBillingDatePrice} (plus applicable promotion and taxes) on the next date of your existing billing cycle, {nextBillingDate}, and {billingCycle} thereafter until you cancel."
        )}
        nextBillingDatePrice={priceDisplay}
        nextBillingDate={nextBillingDate}
        billingCycle={isCourseraPlusAnnual ? _t('yearly') : _t('monthly')}
      />
    </strong>
  );

  return (
    <div>
      <p>
        <Typography2 component="p" variant="bodySecondary">
          <FormattedMessage
            message={_t(
              'By clicking "Reactivate your subscription," you agree to the terms below, {termsOfUseLink}, and {refundPolicyLink}.'
            )}
            termsOfUseLink={getTermsOfUseLink()}
            refundPolicyLink={getPrivacyPolicy()}
          />
        </Typography2>
      </p>
      <p>
        <Typography2 component="p" variant="bodySecondary">
          <FormattedMessage
            message={_t(
              '{youWillBeCharged} To avoid future charges, you can cancel any time in your account settings.'
            )}
            youWillBeCharged={youWillBeCharged}
          />
        </Typography2>
      </p>
    </div>
  );
};

const getCourseraPlusSubscriptionTitle = (isCourseraPlusAnnual: boolean) => {
  return isCourseraPlusAnnual ? (
    <strong>{_t('Your Coursera Plus annual subscription')}</strong>
  ) : (
    <strong>{_t('Your Coursera Plus monthly subscription')}</strong>
  );
};
const ReactivateSubscriptionSteps = ({
  nextBillingDate,
  priceDisplay,
  subscriptionProductType,
  isCourseraPlusAnnual,
}: {
  nextBillingDate: string;
  priceDisplay: JSX.Element;
  subscriptionProductType: ProductType;
  isCourseraPlusAnnual: boolean;
}) => {
  return (
    <ul css={styles.stepWrapper}>
      <li css={styles.step}>
        <span css={styles.stepIcon}>
          <SuccessIcon color="interactive" />
        </span>
        <Typography2 component="p">
          <FormattedMessage
            message={_t('{subscriptionProductTypeCopy} reactivates today')}
            subscriptionProductTypeCopy={
              subscriptionProductType === COURSERA_PLUS_SUBSCRIPTION ? (
                getCourseraPlusSubscriptionTitle(isCourseraPlusAnnual)
              ) : (
                <strong>{_t('Your Specialization subscription')}</strong>
              )
            }
          />
        </Typography2>
      </li>
      <li css={styles.step}>
        <span css={[styles.stepIcon, styles.lastIcon]}>
          <AnswerCorrectIcon color="interactive" />
        </span>
        <Typography2 component="p">
          <FormattedMessage
            message={_t('{payPricePerMonth} starting on {nextBillingDate}')}
            payPricePerMonth={
              <strong>
                <FormattedMessage
                  message={_t('Start paying {subscriptionPrice} a {billingCycle}')}
                  subscriptionPrice={priceDisplay}
                  billingCycle={isCourseraPlusAnnual ? _t('year') : _t('month')}
                />
              </strong>
            }
            nextBillingDate={nextBillingDate}
          />
        </Typography2>
      </li>
    </ul>
  );
};

export type PropsToComponent = {
  subscription: SubscriptionsV1;
  monthlySubscriptionPrice?: { amount: number; currencyCode: string };
  onClose: () => void;
  isOpen: boolean;
  onReactivationSuccess?: () => void;
  nextBillingDate?: string;
  eligibleSubscriptionIds?: string[];
};

const ReactivateSubscriptionModal = ({
  subscription,
  monthlySubscriptionPrice, // in case of is c+ annual, price is annual
  onClose,
  isOpen,
  onReactivationSuccess,
  nextBillingDate,
  eligibleSubscriptionIds,
}: PropsToComponent) => {
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [ctaLoading, setCtaLoading] = useState(false);
  const retrackedTrack = useRetracked();
  const { reactivateSubscription } = useReactivateCanceledSubscription();
  const { Content, HeadingGroup, Actions } = Dialog;

  const { reactivateEventingV2Props, trackClickReactivateConifrm, useGetReactivateConfirmTrackingRef } =
    useEventTracking(subscription, eligibleSubscriptionIds);
  const reactivateConfirmTrackingRef = useGetReactivateConfirmTrackingRef();

  if (
    !nextBillingDate ||
    !subscription ||
    !monthlySubscriptionPrice ||
    !isCourseraPlusOrIsSpecializationSubscription(subscription)
  ) {
    return null;
  }

  const isCourseraPlusAnnual = Boolean(
    subscription.isCourseraPlusSubscription && subscription.productSku && isYearly(subscription.productSku)
  );

  const priceDisplay = getPriceDisplayFromAmount(
    monthlySubscriptionPrice.amount,
    monthlySubscriptionPrice.currencyCode
  );

  // TODO: Implement this once Stripe begins returning this data.
  let lastFourCreditCardDigits;
  const handleReactivation = () => {
    setCtaLoading(true);
    trackClickReactivateConifrm();

    reactivateSubscription(subscription.id)
      .then(() => {
        onReactivationSuccess?.();
      })
      .catch(() => {
        setCtaLoading(false);
        setShowErrorMessage(true);
      });
  };

  const handleClose = () => {
    onClose();
    retrackedTrack({
      trackingData: { subscription },
      trackingName: 'close_reactivate_subscription_modal',
      action: 'click',
    });
  };
  return (
    <Dialog
      aria-describedby="dialog-content"
      open={isOpen}
      variant="standard"
      width="medium"
      onClose={handleClose}
      css={styles.dialog}
      data-testid="reactivate-subscription-modal"
    >
      <HeadingGroup css={styles.title}>{_t('What to expect when you reactivate')}</HeadingGroup>
      <Content id="dialog-content" css={styles.content}>
        <TrackedDiv
          trackingName="reactivate_subscription_modal"
          trackClicks={true}
          withVisibilityTracking={true}
          data={{ subscription }}
        >
          <ReactivateSubscriptionSteps
            nextBillingDate={nextBillingDate}
            priceDisplay={priceDisplay}
            subscriptionProductType={subscription.productType}
            isCourseraPlusAnnual={isCourseraPlusAnnual}
          />
          <div css={styles.paymentDetails}>
            <Typography variant="h2semibold" css={styles.paymentDetailsTitle}>
              {_t('Payment Details')}
            </Typography>
            <div css={styles.secureTextWrapper}>
              <span css={styles.lockIcon}>
                <LockIcon size="small" color="success" />
              </span>
              <Typography2 component="p" css={styles.grayText}>
                {_t('Your payments are processed securely')}
              </Typography2>
            </div>
            {lastFourCreditCardDigits && (
              <div css={styles.walletDetailsRow}>
                <Typography2 component="p">{_t('Pay with')}</Typography2>
                <Typography2 component="p">{lastFourCreditCardDigits}</Typography2>
              </div>
            )}
            <div css={styles.todaysTotalRow}>
              <Typography2 component="h3" variant="subtitleMedium">
                {_t("Today's total: ")}
              </Typography2>
              <div css={styles.todaysPrice}>
                <Typography2 variant="subtitleMedium" component="span">
                  {getPriceDisplayFromAmount(0, monthlySubscriptionPrice.currencyCode)}
                </Typography2>
                <Typography2 component="p" css={styles.grayText}>
                  {isCourseraPlusAnnual ? (
                    <FormattedMessage message={_t('then {priceDisplay}/year')} priceDisplay={priceDisplay} />
                  ) : (
                    <FormattedMessage message={_t('then {priceDisplay}/month')} priceDisplay={priceDisplay} />
                  )}
                </Typography2>
              </div>
            </div>
          </div>
          <div data-testid="legal-copy">
            <ReactivateSubscriptionLegalCopy
              priceDisplay={priceDisplay}
              nextBillingDate={nextBillingDate}
              isCourseraPlusAnnual={isCourseraPlusAnnual}
            />
          </div>
        </TrackedDiv>
        {showErrorMessage && (
          <InlineNotification
            severity="error"
            onDismiss={() => {
              setShowErrorMessage(false);
            }}
          >
            {_t('An internal error occurred. Please try again.')}
          </InlineNotification>
        )}
      </Content>
      <Actions css={styles.action}>
        <TrackedButton
          onClick={handleReactivation}
          loading={ctaLoading}
          disabled={ctaLoading}
          variant="primary"
          size="medium"
          data-testid="reactivate-subscription-confirm"
          trackingName="reactivate_subscription_confirm"
          trackingData={reactivateEventingV2Props}
        >
          <span ref={reactivateConfirmTrackingRef}>{_t('Reactivate your subscription')}</span>
        </TrackedButton>
      </Actions>
    </Dialog>
  );
};

export default ReactivateSubscriptionModal;
